import { DT } from "../../constants.ts";
import { add, flatBasis, norm, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyHeal, applyStatus, damageBarrier, isEnemy, lockOn, moveProjectile, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, ownerAlive, ready, secs, trigger, useUlt } from "../kitlib.ts";

export const V = { auraTarget: 0, skate: 1, iceArts: 2, ultMode: 3 } as const;
export const PK = { Ice: pk(HeroId.LunaSnowcone, 0), Freeze: pk(HeroId.LunaSnowcone, 1), IceArts: pk(HeroId.LunaSnowcone, 2) } as const;

const def: HeroDef = {
  id: HeroId.LunaSnowcone,
  slug: "luna-snowcone",
  name: "Luna Snowcone",
  role: Role.Support,
  color: "#7fd6ff",
  blurb: "Pop-idol support whose ice bursts heal allies, freeze enemies, skate forward, and turn the fight into a concert aura.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.38,
  height: 1.8,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 30,
  reloadTime: 1.5,
  ultCost: 4800,
  abilities: {
    [Slot.Primary]: { name: "Sweet Freeze", key: "LMB", description: "Fire three 24-point ice notes that heal allies or damage enemies.", cooldown: 0 },
    [Slot.Secondary]: { name: "Brain Freeze", key: "RMB", description: "Launch a snowball that deals 50 damage and freezes the first enemy hit for 2.7s.", cooldown: 12 },
    [Slot.Ability1]: { name: "Encore Icicles", key: "Shift", description: "For 6s, fire 50-damage wide ice that heals allies for 75 and heal yourself.", cooldown: 15 },
    [Slot.Ability2]: { name: "Spotlight Share", key: "E", description: "Mark an ally so they receive echo healing when you heal others.", cooldown: 0 },
    [Slot.Ultimate]: { name: "Two-Scoop Finale", key: "Q", description: "Dance for 10s in an aura; press Q again to swap healing and 40% damage boost.", cooldown: 0 },
  },
  passives: [
    { name: "Smooth Spoon", description: "Move forward without firing to skate faster and jump higher." },
    { name: "Chill Heart", description: "Casting Brain Freeze or Encore Icicles restores health." },
  ],
};

function fireIce(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number, input: Parameters<HeroModule["kit"]["tick"]>[2], arts: boolean) {
  const c = sim.c;
  const a = aim(sim, eid);
  const count = arts ? 1 : 3;
  for (let i = 0; i < count; i++) {
    const offset = (i - (count - 1) / 2) * 0.025;
    const dir = norm(add(a.dir, scale(flatBasis(input.yaw).right, offset)));
    spawnProjectile(sim, eid, { kind: arts ? PK.IceArts : PK.Ice, origin: add(a.origin, scale(dir, 0.45)), vel: scale(dir, arts ? 120 : 95), radius: arts ? 1 : 0.08, seconds: arts ? 40 / 120 : 1.1, seq: input.seq });
  }
  c.Weapon.fireCd[eid] = arts ? secs(1 / 1.43) : secs(0.5);
  action(sim, eid, ActionKind.Fire, arts ? 5 : 8);
}

function echoAuraHeal(sim: Parameters<HeroModule["kit"]["tick"]>[0], owner: number, healedTarget: number, amount: number) {
  const id = hv(sim.c, owner, V.auraTarget);
  if (!id) return;
  const e = sim.eidOf(id);
  if (e !== undefined && e !== healedTarget && sim.c.Player.alive[e] && !isEnemy(sim, owner, e)) applyHeal(sim, owner, e, amount * 0.4, { silent: true });
}

const module: HeroModule = {
  def,
  kit: {
    init() {},
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.iceArts) > 0) setHv(c, eid, V.iceArts, Math.max(0, hv(c, eid, V.iceArts) - DT));
      const skating = canAct && input.moveZ > 0.75 && !(input.buttons & (Btn.Primary | Btn.Secondary)) && c.Weapon.fireCd[eid]! <= 0;
      setHv(c, eid, V.skate, skating ? Math.min(1, hv(c, eid, V.skate) + DT * 2) : Math.max(0, hv(c, eid, V.skate) - DT * 3));
      if (c.Ult.active[eid]! > 0) {
        const healMode = hv(c, eid, V.ultMode) < 0.5;
        for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), 10, (e) => !isEnemy(sim, eid, e), false)) {
          if (healMode) applyHeal(sim, eid, t, 25 * DT * 10, { silent: true });
          else applyStatus(sim, t, "boost", 0.2, { source: eid, boostPct: 0.4 });
          applyStatus(sim, t, "haste", 0.2, { source: eid, hastePct: 0.4 });
          applyStatus(sim, t, "resist", 0.2, { source: eid });
        }
        if (pressed & Btn.Ultimate) setHv(c, eid, V.ultMode, healMode ? 1 : 0);
      }
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) {
        c.Ult.active[eid] = secs(10);
        setHv(c, eid, V.ultMode, 0);
      }
      if (pressed & Btn.Ability2) {
        const t = lockOn(sim, eid, 30, 0.25, true);
        setHv(c, eid, V.auraTarget, t === undefined ? 0 : c.Net.id[t]!);
        action(sim, eid, ActionKind.Cast, 12);
      }
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        setHv(c, eid, V.iceArts, 6);
        applyHeal(sim, eid, eid, 105);
        action(sim, eid, ActionKind.Cast, 12);
      }
      if (pressed & Btn.Secondary && ready(sim, eid, Slot.Secondary)) {
        consume(sim, eid, Slot.Secondary);
        applyHeal(sim, eid, eid, 105);
        const a = aim(sim, eid);
        spawnProjectile(sim, eid, { kind: PK.Freeze, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 80), radius: 0.3, seconds: 1.5, seq: input.seq });
        action(sim, eid, ActionKind.AltFire, 15);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary), hv(c, eid, V.iceArts) > 0)) fireIce(sim, eid, input, hv(c, eid, V.iceArts) > 0);
    },
    movement(sim, eid, _input, mods) {
      const k = hv(sim.c, eid, V.skate);
      if (k > 0) {
        mods.speedMul *= 1 + 0.6 * k;
        mods.canJump = true;
      }
    },
  },
  projectiles: {
    [PK.Ice]: {
      visual: { color: "#9eeaff", size: 0.1, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "both");
        if (hit) {
          if (hit.target !== undefined && ownerAlive(sim, owner)) {
            if (isEnemy(sim, owner, hit.target)) applyDamage(sim, hit.target, 24, owner, { crit: hit.head, pos: hit.pos });
            else {
              const h = applyHeal(sim, owner, hit.target, 24);
              echoAuraHeal(sim, owner, hit.target, h);
            }
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 24, owner);
          else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
          sim.destroyEntity(p);
          return;
        }
        if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
    [PK.Freeze]: {
      visual: { color: "#d7fbff", size: 0.28, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "enemies");
        if (hit) {
          if (hit.target !== undefined && ownerAlive(sim, owner)) {
            applyDamage(sim, hit.target, 50, owner, { pos: hit.pos });
            applyStatus(sim, hit.target, "frozen", 2.7, { source: owner });
            addOverhealth(sim, owner, 50, secs(3), owner);
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 50, owner);
          else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
          sim.destroyEntity(p);
          return;
        }
        if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
    [PK.IceArts]: {
      visual: { color: "#cfffff", size: 1, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "both");
        if (hit) {
          if (hit.target !== undefined && ownerAlive(sim, owner)) {
            if (isEnemy(sim, owner, hit.target)) applyDamage(sim, hit.target, 50, owner, { pos: hit.pos });
            else {
              const h = applyHeal(sim, owner, hit.target, 75);
              echoAuraHeal(sim, owner, hit.target, h);
            }
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 50, owner);
          else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
          sim.destroyEntity(p);
          return;
        }
        if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
};

export default module;
